/* ────────────────────────────────────────────────────────────── 
   src/pages/reports/alumnos/AlumnoProyectoMensual.tsx
   ────────────────────────────────────────────────────────────── */
import { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

import {
  Stack,
  Button,
  TextField,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';

import { useAlumnoProyectoMensualReport } from '../../../hooks/useReportJob';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';
import api from '../../../api/axios';
import type { AlumnoProyectoMensualRow } from '../../../api/reports';

export default function AlumnoProyectoMensual() {
  const { t } = useTranslation<'translation'>();
  const today = dayjs();

  const [alumnoId, setAlumnoId] = useState<number | string>('');
  const [desde, setDesde] = useState(today.startOf('year').format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(today.format('YYYY-MM-DD'));

  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useAlumnoProyectoMensualReport();
  const { data: status } = useStatus(jobId);

  const pendiente = !!status && !status.done && !status.failed;
  const rows: AlumnoProyectoMensualRow[] = status?.result?.rows ?? [];

  /* ───────── alumnos reales ───────── */
  const [usuariosRaw, setUsuariosRaw] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [loadingAlumnos, setLoadingAlumnos] = useState(true);

  const isActiveTrue = (v: any) => {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (['1', 'true', 'si', 'sí', 'activo', 'active', 'enabled', 'yes'].includes(s)) return true;
      if (['0', 'false', 'no', 'inactivo', 'inactive', 'baja', 'archivado', 'disabled'].includes(s))
        return false;
    }
    return !!v;
  };

  useEffect(() => {
    setLoadingAlumnos(true);
    api
      .get('/usuarios')
      .then((res) => setUsuariosRaw(res.data))
      .finally(() => setLoadingAlumnos(false));
  }, []);

  useEffect(() => {
    const soloClientes = usuariosRaw.filter((u) => u.rol === 'cliente');
    setClientes(soloClientes);
  }, [usuariosRaw]);

  /* ───────── pivot mensual por proyecto (filtrado por rango) ───────── */
  const { months, data } = useMemo(() => {
    const map: Record<string, any> = {};
    const monthMap = new Map<string, string>();
    const desdeDate = dayjs(desde);
    const hastaDate = dayjs(hasta);

    rows.forEach((r) => {
      const monthDate = dayjs(r.monthLabel, 'MMM YYYY'); // ajustar formato si necesario
      if (!monthDate.isBetween(desdeDate.startOf('month'), hastaDate.endOf('month'), null, '[]'))
        return;

      monthMap.set(r.monthKey, r.monthLabel);
      const key = `${r.proyectoId ?? 'legacy'}::${r.proyecto}`;

      if (!map[key]) {
        map[key] = {
          proyecto: r.proyecto,
          proyectoId: r.proyectoId ?? null,
        };
      }
      if (!map[key][r.monthKey]) map[key][r.monthKey] = 0;
      map[key][r.monthKey] += r.horas;
    });

    const months = Array.from(monthMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, label]) => ({ key, label }));

    return { months, data: Object.values(map) };
  }, [rows, desde, hasta]);

  /* ───────── generar reporte ───────── */
  const handleGenerate = async () => {
    if (!alumnoId) return;

    const alumnoIdNumber = typeof alumnoId === 'string' ? Number(alumnoId) : alumnoId;
    if (isNaN(alumnoIdNumber)) {
      console.warn('Alumno inválido, no se puede generar informe');
      return;
    }

    try {
      const result = await run({ alumnoId: alumnoIdNumber, desde, hasta });
      setJobId(result.jobId);
    } catch (err) {
      console.error('Error generando informe:', err);
    }
  };

  /* ───────── descargar Excel ───────── */
  const handleDownload = async () => {
    if (!status?.result?.url) return;

    const desdeDate = dayjs(desde);
    const hastaDate = dayjs(hasta);

    const excelDataMap: Record<string, { Proyecto: string; Mes: string; Horas: number }> = {};

    data.forEach((d) => {
      months.forEach((m) => {
        const monthDate = dayjs(m.label, 'MMM YYYY');
        if (!monthDate.isBetween(desdeDate.startOf('month'), hastaDate.endOf('month'), null, '[]'))
          return;

        const horas = d[m.key] ?? 0;
        if (horas === 0) return;

        const key = `${d.proyectoId ?? 'legacy'}-${d.proyecto}-${m.label}`;
        if (!excelDataMap[key]) {
          excelDataMap[key] = { Proyecto: d.proyecto, Mes: m.label, Horas: 0 };
        }
        excelDataMap[key].Horas += horas;
      });
    });

    const excelData = Object.values(excelDataMap);

    const worksheet = XLSX.utils.json_to_sheet(excelData, { header: ['Proyecto', 'Mes', 'Horas'] });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'HorasClientesMensuales');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const randomString = () => Math.random().toString(36).substring(2, 10);
    const filename = `alumno-proyecto-cli-${randomString()}-${randomString()}.xlsx`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('alumnoProyectoMensual.titulo')}</Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Autocomplete
          freeSolo
          size="small"
          options={clientes}
          getOptionLabel={(option) =>
            typeof option === 'string'
              ? option
              : `${option.nombreCompleto || `${option.nombre} ${option.apellido}`}${
                  isActiveTrue(option.activo) ? '' : ' (Inactivo)'
                }`
          }
          value={
            typeof alumnoId === 'number' ? clientes.find((c) => c.id === alumnoId) || '' : alumnoId
          }
          onChange={(e, newValue) => {
            if (typeof newValue === 'string') setAlumnoId(newValue);
            else if (newValue) setAlumnoId(newValue.id);
            else setAlumnoId('');
          }}
          inputValue={
            typeof alumnoId === 'number'
              ? clientes.find((c) => c.id === alumnoId)?.nombreCompleto || ''
              : alumnoId
          }
          onInputChange={(e, newInputValue) => setAlumnoId(newInputValue)}
          loading={loadingAlumnos}
          sx={{ minWidth: 250, mt: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('alumnoProyectoMensual.filtros.alumno')}
              size="small"
              margin="dense"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingAlumnos ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          type="date"
          label={t('alumnoProyectoMensual.filtros.desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          type="date"
          label={t('alumnoProyectoMensual.filtros.hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Button variant="contained" onClick={handleGenerate}>
          {t('alumnoProyectoMensual.filtros.generar')}
        </Button>
      </Stack>

      {pendiente && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status!.progress} />
          <Typography variant="body2">
            {t('alumnoProyectoMensual.estado.generando', { progress: status!.progress })}
          </Typography>
        </Stack>
      )}

      {status?.done && status.result && (
        <Alert severity="success">
          {t('alumnoProyectoMensual.estado.exito')}{' '}
          <Button
            variant="text"
            size="small"
            onClick={handleDownload}
            sx={{ textTransform: 'none', textDecoration: 'underline', p: 0 }}
          >
            {t('alumnoProyectoMensual.estado.descargar')}
          </Button>{' '}
          {t('alumnoProyectoMensual.estado.filas', { count: status.result.registros })}
        </Alert>
      )}

      {status?.failed && <Alert severity="error">{t('alumnoProyectoMensual.estado.error')}</Alert>}

      {status?.done && (
        <Paper>
          {!data.length ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        bgcolor: '#000',
                        color: '#fff',
                        fontWeight: 'bold',
                      }}
                    >
                      {t('alumnoProyectoMensual.tabla.proyecto')}
                    </TableCell>
                    {months.map((m) => (
                      <TableCell
                        key={m.key}
                        align="right"
                        sx={{
                          whiteSpace: 'nowrap',
                          bgcolor: '#000',
                          color: '#fff',
                          fontWeight: 'bold',
                        }}
                      >
                        {m.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.map((row: any) => (
                    <TableRow key={`${row.proyectoId ?? 'legacy'}-${row.proyecto}`}>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          bgcolor: 'background.paper',
                        }}
                      >
                        {row.proyecto}
                      </TableCell>
                      {months.map((m) => (
                        <TableCell key={m.key} align="right">
                          {row[m.key] ?? 0}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Stack>
  );
}
